import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Card, CardContent } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from '../../api/axiosConfig';
// Removed expenses management widgets (moved to Expense Management Dashboard)
import { ApiResult } from '../../types/auth';
import { StockDto, InvestmentType, CurrencyCode } from '../../types/stocks';
import { investmentTypeToLabel } from '../../utils/investmentType';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';
import { formatEuro, formatNumber } from '../../utils/currency';

interface Stock {
  publicId: string;
  symbol: string;
  invested: number;
  grossDividends: number;
  taxPaid: number;
  netDividends: number;
  currentValue: number;
  totalReturn: number;
  percentReturn: number;
}

interface PortfolioSummary {
  totalInvested: number;
  totalValue: number;
  totalReturn: number;
  avgReturn: number;
  grossDividends: number;
  netDividends: number;
  taxPaid: number;
  avgDividendYield: number;
}

const COLORS = ['#1976d2', '#ff9800', '#388e3c', '#d32f2f', '#7b1fa2'];

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [typeAllocation, setTypeAllocation] = useState<{ name: string; value: number; percentage: number }[]>([]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        // Fetch stocks; the summary is computed below from the same data
        const stocksRes = await axios.get<ApiResult<StockDto[]>>('/api/stock');
        // Unwrap .data from ApiResult envelope
        const stocksArr = stocksRes.data?.data ?? [];
        // Compute all required fields from raw API data (EUR-consistent). Account for sales when calculating remaining quantity and EUR cost basis.
        const stocksWithComputed = (stocksArr as any[]).map(s => {
          const purchases = Array.isArray(s.purchases) ? s.purchases : [];
          const sales = Array.isArray(s.sales) ? s.sales : [];
          const currency: CurrencyCode = s.currency as CurrencyCode;
          // Prefer currentCambio; if missing for non-EUR, fallback to average purchase cambio
          let stockCambio: number = typeof s.currentCambio === 'number' ? Number(s.currentCambio) : (currency === CurrencyCode.EUR ? 1 : 0);
          if (currency !== CurrencyCode.EUR && (!stockCambio || stockCambio === 0)) {
            const cambios: number[] = purchases
              .map((p: any) => (typeof p.cambio === 'number' ? Number(p.cambio) : (typeof p.pricePerShareEUR === 'number' && p.pricePerShare ? Number(p.pricePerShareEUR) / Number(p.pricePerShare) : NaN)))
              .filter((v: number) => Number.isFinite(v) && v > 0) as number[];
            if (cambios.length > 0) {
              stockCambio = cambios.reduce((a: number, b: number) => a + b, 0) / cambios.length;
            }
          }

          // Map of purchasePublicId -> total quantity sold from that purchase
          const soldByPurchase = new Map<string, number>();
          sales.forEach((sale: any) => {
            const spList = Array.isArray(sale.salePurchases) ? sale.salePurchases : [];
            spList.forEach((sp: any) => {
              const pid = sp.purchasePublicId;
              const qty = Number(sp.quantitySold || 0);
              if (pid) soldByPurchase.set(pid, (soldByPurchase.get(pid) || 0) + qty);
            });
          });

          // Remaining quantity and remaining EUR cost basis after sales (FIFO via salePurchases breakdown)
          let remainingQuantity = 0;
          let remainingCostBasisEUR = 0;
          purchases.forEach((p: any) => {
            const soldQty = soldByPurchase.get(p.publicId) || 0;
            const totalQty = Number(p.quantity || 0);
            const boundedSold = Math.min(totalQty, Number(soldQty));
            const remQty = Math.max(0, totalQty - boundedSold);
            remainingQuantity += remQty;
            const perShareEUR = typeof p.pricePerShareEUR === 'number'
              ? Number(p.pricePerShareEUR)
              : Number(p.pricePerShare || 0) * (typeof p.cambio === 'number' ? Number(p.cambio) : (currency === CurrencyCode.EUR ? 1 : stockCambio));
            remainingCostBasisEUR += remQty * perShareEUR;
          });

          // Dividends
          const grossDividends = Array.isArray(s.dividends)
            ? Number(s.dividends.reduce((sum: number, d: any) => sum + (Number(d.grossAmount || 0)), 0).toFixed(2))
            : 0;
          const taxPaid = Array.isArray(s.dividends)
            ? Number(s.dividends.reduce((sum: number, d: any) => sum + (Number(d.taxPaid || 0)), 0).toFixed(2))
            : 0;
          const netDividends = Array.isArray(s.dividends)
            ? Number(s.dividends.reduce((sum: number, d: any) => sum + (Number(d.amount || 0)), 0).toFixed(2))
            : 0;

          // Current Value (EUR) uses remaining quantity and current cambio
          const currentPriceCoin = Number(s.currentPrice || 0);
          const effectiveCambio = currency === CurrencyCode.EUR ? 1 : (stockCambio && stockCambio > 0 ? stockCambio : 1);
          const currentValue = Number((remainingQuantity * currentPriceCoin * effectiveCambio).toFixed(2));

          // Invested should reflect remaining EUR cost basis (not total historical purchases)
          const invested = Number(remainingCostBasisEUR.toFixed(2));

          // Returns
          const noDivReturn = Number((currentValue - invested).toFixed(2));
          const totalReturn = Number((noDivReturn + netDividends).toFixed(2));
          const percentReturn = invested > 0 ? Number(((totalReturn / invested) * 100).toFixed(2)) : 0;

          return {
            ...s,
            invested,
            grossDividends,
            taxPaid,
            netDividends,
            currentValue,
            totalReturn,
            percentReturn,
            noDivReturn
          };
        });
        setStocks(stocksWithComputed);

        // Compute allocation by InvestmentType enum using currentValue (fallback invested if currentValue=0)
        const totalValue = stocksWithComputed.reduce((sum, s) => sum + (typeof s.currentValue === 'number' ? s.currentValue : 0), 0);
        const typeMap = new Map<string, number>();
        stocksWithComputed.forEach(s => {
          const enumVal: InvestmentType | undefined = (s as any).investmentType;
          const typeName = investmentTypeToLabel(enumVal, s.symbol);
          const val = (typeof s.currentValue === 'number' ? s.currentValue : 0);
          typeMap.set(typeName, (typeMap.get(typeName) || 0) + val);
        });
        const allocation = Array.from(typeMap.entries()).map(([name, value]) => ({
          name,
          value,
          percentage: totalValue > 0 ? (value / totalValue) * 100 : 0
        })).sort((a,b)=> b.value - a.value);
        setTypeAllocation(allocation);
        // Compute summary locally from EUR-consistent stock data for accuracy
        const totalInvestedLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.invested === 'number' ? s.invested : 0), 0);
        const totalValueLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.currentValue === 'number' ? s.currentValue : 0), 0);
        const totalReturnLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.totalReturn === 'number' ? s.totalReturn : 0), 0);
        const grossDividendsLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.grossDividends === 'number' ? s.grossDividends : 0), 0);
        const netDividendsLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.netDividends === 'number' ? s.netDividends : 0), 0);
        const taxPaidLocal = stocksWithComputed.reduce((sum, s) => sum + (typeof s.taxPaid === 'number' ? s.taxPaid : 0), 0);
        const avgReturnPctLocal = totalInvestedLocal > 0 ? (totalReturnLocal / totalInvestedLocal) * 100 : 0;
        const avgDividendYieldLocal = totalInvestedLocal > 0 ? (netDividendsLocal / totalInvestedLocal) * 100 : 0;
        const safeSummary = {
          totalInvested: Number(totalInvestedLocal.toFixed(2)),
          totalValue: Number(totalValueLocal.toFixed(2)),
          totalReturn: Number(totalReturnLocal.toFixed(2)),
          avgReturn: Number(avgReturnPctLocal.toFixed(2)),
          grossDividends: Number(grossDividendsLocal.toFixed(2)),
          netDividends: Number(netDividendsLocal.toFixed(2)),
          taxPaid: Number(taxPaidLocal.toFixed(2)),
          avgDividendYield: Number(avgDividendYieldLocal.toFixed(2))
        };
        // Sort stocks by symbol for consistent display/order across widgets
        const sortedBySymbol = [...stocksWithComputed].sort((a,b)=> (a.symbol||'').localeCompare(b.symbol||''));
        setStocks(sortedBySymbol);
        setSummary(safeSummary);
      } catch (err) {
        // Optionally handle error
        setStocks([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader title="Análise da carteira" />
      {/* Summary Cards and Table */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <AutoGrid min={160}>
          <Box>
            <Typography variant="subtitle2">Total investido:</Typography>
            <Typography>
              {typeof summary?.totalInvested === 'number' ? formatEuro(summary.totalInvested) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Valor total:</Typography>
            <Typography style={{ color: summary && typeof summary.totalValue === 'number' && typeof summary.totalInvested === 'number' && summary.totalValue >= summary.totalInvested ? '#388e3c' : '#d32f2f', fontWeight: 500 }}>
              {typeof summary?.totalValue === 'number' ? formatEuro(summary.totalValue) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Retorno total:</Typography>
            <Typography style={{ color: summary && typeof summary.totalReturn === 'number' && summary.totalReturn > 0 ? '#388e3c' : summary && typeof summary.totalReturn === 'number' && summary.totalReturn < 0 ? '#d32f2f' : undefined, fontWeight: 500 }}>
              {typeof summary?.totalReturn === 'number' ? formatEuro(summary.totalReturn) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Retorno médio (%):</Typography>
            <Typography style={{ color: summary && typeof summary.avgReturn === 'number' && summary.avgReturn > 0 ? '#388e3c' : summary && typeof summary.avgReturn === 'number' && summary.avgReturn < 0 ? '#d32f2f' : undefined, fontWeight: 500 }}>
              {typeof summary?.avgReturn === 'number' ? `${formatNumber(summary.avgReturn)}%` : '--'}
            </Typography>
          </Box>
        </AutoGrid>
        <AutoGrid min={160} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="subtitle2">Dividendos brutos:</Typography>
            <Typography style={{ color: '#388e3c', fontWeight: 500 }}>
              {typeof summary?.grossDividends === 'number' ? formatEuro(summary.grossDividends) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Dividendos líquidos:</Typography>
            <Typography style={{ color: '#388e3c', fontWeight: 500 }}>
              {typeof summary?.netDividends === 'number' ? formatEuro(summary.netDividends) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Imposto pago:</Typography>
            <Typography style={{ color: '#d32f2f', fontWeight: 500 }}>
              {typeof summary?.taxPaid === 'number' ? formatEuro(summary.taxPaid) : '--'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">Rendimento médio de dividendos:</Typography>
            <Typography style={{ color: summary && typeof summary.avgDividendYield === 'number' && summary.avgDividendYield > 0 ? '#388e3c' : summary && typeof summary.avgDividendYield === 'number' && summary.avgDividendYield < 0 ? '#d32f2f' : undefined, fontWeight: 500 }}>
              {typeof summary?.avgDividendYield === 'number' ? `${formatNumber(summary.avgDividendYield)}%` : '--'}
            </Typography>
          </Box>
        </AutoGrid>
      </Paper>
      {/* Stocks Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Símbolo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Investido (€)</TableCell>
              <TableCell>Dividendos brutos</TableCell>
              <TableCell>Imposto pago</TableCell>
              <TableCell>Dividendos líquidos</TableCell>
              <TableCell>Valor atual (€)</TableCell>
              <TableCell>Retorno total (€)</TableCell>
              <TableCell>% Retorno</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stocks.map(s => {
              const typeLabel = investmentTypeToLabel((s as any).investmentType, s.symbol);
              return (
              <TableRow key={s.publicId}>
                <TableCell>{s.symbol}</TableCell>
                <TableCell>{typeLabel}</TableCell>
                <TableCell>{typeof s.invested === 'number' ? formatNumber(s.invested) : '--'}</TableCell>
                <TableCell>{typeof s.grossDividends === 'number' ? formatNumber(s.grossDividends) : '--'}</TableCell>
                <TableCell>{typeof s.taxPaid === 'number' ? formatNumber(s.taxPaid) : '--'}</TableCell>
                <TableCell>{typeof s.netDividends === 'number' ? formatNumber(s.netDividends) : '--'}</TableCell>
                <TableCell>{typeof s.currentValue === 'number' ? formatNumber(s.currentValue) : '--'}</TableCell>
                <TableCell style={{ color: typeof s.totalReturn === 'number' && s.totalReturn > 0 ? '#388e3c' : typeof s.totalReturn === 'number' && s.totalReturn < 0 ? '#d32f2f' : undefined, fontWeight: 500 }}>{typeof s.totalReturn === 'number' ? formatNumber(s.totalReturn) : '--'}</TableCell>
                <TableCell style={{ color: typeof s.percentReturn === 'number' && s.percentReturn > 0 ? '#388e3c' : typeof s.percentReturn === 'number' && s.percentReturn < 0 ? '#d32f2f' : undefined, fontWeight: 500 }}>{typeof s.percentReturn === 'number' ? `${formatNumber(s.percentReturn)}%` : '--'}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Charts in a single responsive row using flexbox */}
      <AutoGrid min={360} sx={{ mb: 2 }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Alocação da carteira (pelo valor atual)</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={stocks} dataKey="currentValue" nameKey="symbol" cx="50%" cy="50%" outerRadius={60} label>
                    {stocks.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatEuro(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Alocação por tipo</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={typeAllocation} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({name, percentage}) => `${name} ${formatNumber(percentage as number, 1)}%`}>
                    {typeAllocation.map((entry, idx) => (
                      <Cell key={`type-cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, _name: string, p: any) => [formatEuro(value), `${p?.payload?.name}`]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Valor atual por ação</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stocks} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="symbol" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatEuro(value)} />
                  <Bar dataKey="currentValue" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Retorno: com e sem dividendos</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stocks} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="symbol" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatEuro(value)} />
                  <Legend />
                  <Bar dataKey="totalReturn" name="Com dividendos" fill="#388e3c" />
                  <Bar dataKey="noDivReturn" name="Sem dividendos" fill="#d32f2f" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      </AutoGrid>
      
      {/* Expenses management analytics moved to dedicated Expense Management Dashboard */}
    </Box>
  );
}
